import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { en } from '@/i18n/en';
import { criarFormatadores, ehIdioma, IDIOMA_PADRAO, IDIOMAS } from '@/i18n/formatacao';
import { ptBR } from '@/i18n/pt-BR';
export const CHAVE_DO_IDIOMA = 'dfp.agro.idioma';
const DICIONARIOS = { 'pt-BR': ptBR, en };
// --- Implementação -------------------------------------------------------------------------------
function interpolar(modelo, parametros, formatarQuantidade) {
    return modelo.replace(/\{(\w+)\}/g, (_trecho, nome) => {
        const valor = parametros[nome];
        if (valor === undefined) {
            throw new Error(`i18n: parâmetro "${nome}" ausente`);
        }
        return nome === 'quantidade' && typeof valor === 'number' ? formatarQuantidade(valor) : String(valor);
    });
}
function criarContexto(idioma, alterarIdioma) {
    const dicionario = DICIONARIOS[idioma];
    const formatar = criarFormatadores(idioma);
    // Sem fallback: chave ausente é erro de implementação e precisa aparecer.
    const t = ((chave, parametros) => {
        const mensagem = dicionario[chave];
        if (mensagem === undefined) {
            throw new Error(`i18n: tradução ausente para "${chave}" (${idioma})`);
        }
        const argumentos = parametros ?? {};
        const texto = typeof mensagem === 'string'
            ? mensagem
            : argumentos.quantidade === 1
                ? mensagem.um
                : mensagem.outros;
        return interpolar(texto, argumentos, formatar.inteiro);
    });
    const traduzirErro = (falha) => {
        if (falha.codigo === 'falha_de_rede') {
            return t('erro.falhaDeRede');
        }
        if (falha.codigo === 'resposta_inesperada' && falha.registro !== undefined) {
            const { status } = falha.registro;
            if (status === 401) {
                return t('erro.status401');
            }
            if (status === 404) {
                return t('erro.status404');
            }
            if (status === 503) {
                return t('erro.status503');
            }
            return t('erro.respostaInesperada', { status });
        }
        return falha.message;
    };
    return { idioma, alterarIdioma, t, formatar, traduzirErro };
}
function lerIdiomaArmazenado() {
    try {
        const armazenado = window.localStorage.getItem(CHAVE_DO_IDIOMA);
        return ehIdioma(armazenado) ? armazenado : IDIOMA_PADRAO;
    }
    catch {
        return IDIOMA_PADRAO;
    }
}
function gravarIdioma(idioma) {
    try {
        window.localStorage.setItem(CHAVE_DO_IDIOMA, idioma);
    }
    catch {
        // Armazenamento indisponível: a preferência vale apenas nesta sessão de página.
    }
}
const Contexto = createContext(null);
/** Provider único de idioma: troca textos e formatações sem remontar a árvore. */
export function I18nProvider({ children }) {
    const [idioma, setIdioma] = useState(lerIdiomaArmazenado);
    const alterarIdioma = useCallback((proximo) => {
        setIdioma(proximo);
        gravarIdioma(proximo);
    }, []);
    useLayoutEffect(() => {
        document.documentElement.lang = idioma;
    }, [idioma]);
    const valor = useMemo(() => criarContexto(idioma, alterarIdioma), [idioma, alterarIdioma]);
    return _jsx(Contexto.Provider, { value: valor, children: children });
}
export function useI18n() {
    const contexto = useContext(Contexto);
    if (contexto === null) {
        throw new Error('useI18n exige I18nProvider acima na árvore');
    }
    return contexto;
}
const ROTULO_CURTO = { 'pt-BR': 'PT', en: 'EN' };
const CHAVE_DA_OPCAO = {
    'pt-BR': 'idioma.opcaoPt',
    en: 'idioma.opcaoEn',
};
/** Seletor compacto PT | EN, usado no cabeçalho global e na tela de login. */
export function SeletorDeIdioma({ className }) {
    const { idioma, alterarIdioma, t } = useI18n();
    return (_jsx("div", { className: className === undefined ? 'seletor-idioma' : `seletor-idioma ${className}`, role: "group", "aria-label": t('idioma.grupo'), children: IDIOMAS.map((opcao) => {
            const ativa = opcao === idioma;
            return (_jsx("button", { type: "button", className: ativa ? 'seletor-idioma__opcao seletor-idioma__opcao--ativa' : 'seletor-idioma__opcao', "aria-pressed": ativa, "aria-label": t(CHAVE_DA_OPCAO[opcao]), title: t(CHAVE_DA_OPCAO[opcao]), lang: opcao, onClick: () => alterarIdioma(opcao), children: ROTULO_CURTO[opcao] }, opcao));
        }) }));
}
